import React from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Centralised setlist state — supports MULTIPLE setlists.
 *
 * Returns:
 *  - setlist (the currently active setlist object)
 *  - setlists (all setlists, lightweight summary list)
 *  - activeId
 *  - addSong / removeSong / clear / setSongIds / setName  (operate on active)
 *  - moveUp / moveDown
 *  - createSetlist(name) / deleteSetlist(id) / activateSetlist(id)
 *  - renameSetlist(id, name)
 *  - reload
 */
export const useSetlist = () => {
  const [setlist, setSetlist] = React.useState({
    id: "default",
    name: "Setlist du soir",
    song_ids: [],
  });
  const [setlists, setSetlists] = React.useState([]);
  const [activeId, setActiveId] = React.useState("default");
  const [loaded, setLoaded] = React.useState(false);

  const reload = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/setlists`);
      const all = data.setlists || [];
      const active = data.active_id;
      setSetlists(all);
      setActiveId(active);
      const activeDoc = all.find((s) => s.id === active);
      if (activeDoc) setSetlist(activeDoc);
    } catch (e) {
      // silent
    } finally {
      setLoaded(true);
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const patchActive = React.useCallback(async (payload) => {
    // Optimistic on active setlist
    setSetlist((prev) => ({ ...prev, ...payload }));
    setSetlists((prev) => prev.map((s) => (s.id === activeId ? { ...s, ...payload } : s)));
    try {
      const { data } = await axios.patch(`${API}/setlist`, payload);
      setSetlist(data);
      setSetlists((prev) => prev.map((s) => (s.id === data.id ? data : s)));
    } catch (e) {
      toast.error("Erreur setlist");
    }
  }, [activeId]);

  const setSongIds = React.useCallback((ids) => patchActive({ song_ids: ids }), [patchActive]);
  const setName = React.useCallback((name) => patchActive({ name }), [patchActive]);

  const addSong = React.useCallback((songId) => {
    setSetlist((prev) => {
      if (prev.song_ids.includes(songId)) return prev;
      const next = { ...prev, song_ids: [...prev.song_ids, songId] };
      axios.patch(`${API}/setlist`, { song_ids: next.song_ids }).catch(() => {});
      setSetlists((arr) => arr.map((s) => (s.id === prev.id ? next : s)));
      return next;
    });
  }, []);

  const removeSong = React.useCallback((songId) => {
    setSetlist((prev) => {
      const next = { ...prev, song_ids: prev.song_ids.filter((id) => id !== songId) };
      axios.patch(`${API}/setlist`, { song_ids: next.song_ids }).catch(() => {});
      setSetlists((arr) => arr.map((s) => (s.id === prev.id ? next : s)));
      return next;
    });
  }, []);

  const move = React.useCallback((songId, delta) => {
    setSetlist((prev) => {
      const idx = prev.song_ids.indexOf(songId);
      if (idx < 0) return prev;
      const target = idx + delta;
      if (target < 0 || target >= prev.song_ids.length) return prev;
      const ids = [...prev.song_ids];
      const tmp = ids[idx]; ids[idx] = ids[target]; ids[target] = tmp;
      axios.patch(`${API}/setlist`, { song_ids: ids }).catch(() => {});
      const next = { ...prev, song_ids: ids };
      setSetlists((arr) => arr.map((s) => (s.id === prev.id ? next : s)));
      return next;
    });
  }, []);

  const clear = React.useCallback(() => patchActive({ song_ids: [] }), [patchActive]);

  // ----- Multi-setlist operations -----
  const createSetlist = React.useCallback(async (name) => {
    try {
      const { data } = await axios.post(`${API}/setlists`, { name: name || "Nouvelle setlist" });
      await axios.post(`${API}/setlists/${data.id}/activate`);
      await reload();
      toast.success(`Setlist « ${data.name} » créée`);
      return data;
    } catch (e) {
      toast.error("Impossible de créer la setlist");
      return null;
    }
  }, [reload]);

  const deleteSetlist = React.useCallback(async (id) => {
    try {
      await axios.delete(`${API}/setlists/${id}`);
      await reload();
      toast.success("Setlist supprimée");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Suppression impossible";
      toast.error(msg);
    }
  }, [reload]);

  const activateSetlist = React.useCallback(async (id) => {
    // Always POST — don't early-return on closure-captured activeId (may be stale)
    try {
      await axios.post(`${API}/setlists/${id}/activate`);
      await reload();
    } catch (e) {
      toast.error("Activation impossible");
    }
  }, [reload]);

  const renameSetlist = React.useCallback(async (id, name) => {
    // Optimistic
    setSetlists((arr) => arr.map((s) => (s.id === id ? { ...s, name } : s)));
    if (id === activeId) setSetlist((prev) => ({ ...prev, name }));
    try {
      await axios.patch(`${API}/setlists/${id}`, { name });
    } catch (e) {
      toast.error("Renommage impossible");
    }
  }, [activeId]);

  const duplicateSetlist = React.useCallback(async (id, name) => {
    try {
      const body = name ? { name } : {};
      const { data } = await axios.post(`${API}/setlists/${id}/duplicate`, body);
      await axios.post(`${API}/setlists/${data.id}/activate`);
      await reload();
      toast.success(`Setlist dupliquée : « ${data.name} »`);
      return data;
    } catch (e) {
      toast.error("Duplication impossible");
      return null;
    }
  }, [reload]);

  return {
    setlist,
    setlists,
    activeId,
    loaded,
    reload,
    addSong,
    removeSong,
    moveUp: (id) => move(id, -1),
    moveDown: (id) => move(id, +1),
    setName,
    setSongIds,
    clear,
    createSetlist,
    deleteSetlist,
    duplicateSetlist,
    activateSetlist,
    renameSetlist,
  };
};
